import { useEffect, useState } from 'react';
import { EGGS_BY_ID } from '@/config/eggs.config';
import { VARIANT_MULT } from '@/domain/types/pet';
import { useGameStore, type HatchBulkResult } from '@/ui/store/gameStore';
import { petAssetUrl } from '@/assets/paths';
import { formatNumberCompact } from '@/utils/formatNumber';
import { eventBus } from '@/shared/EventBus';
import type { ID } from '@/domain/types/common';

/**
 * HatchAnimationModal — kontroler hatcha + finalny panel wyniku (sesja 29 — Step A).
 *
 * **Po refaktorze (sesja 29):**
 * Komponent NIE animuje już sam. Phaser `HatchScene` odgrywa cinematic
 * (egg pulse → burst → pet reveal), a ten komponent jest:
 *
 *  1. **Kontroler** — gdy `hatchResult` zostaje ustawiony, emituje
 *     `hatch:start` do Phasera i ustawia `hatchPhase='animating'`.
 *     App.tsx widzi `animating` → ukrywa HUD/joystick/modale (Wariant A
 *     z PHASER_ANIMATION_ANALYSIS).
 *
 *  2. **Final panel** — gdy Phaser emituje `hatch:complete`, ustawiamy
 *     `hatchPhase='showResult'` i renderujemy panel z nazwą, rarity,
 *     variant, power i przyciskiem "Świetnie!". Close button czyści
 *     hatchResult/hatchPhase i emituje `hatch:closed`.
 *
 * **Legacy CSS** keyframes (pulse-egg, burst-egg, reveal-pet, show-pet-idle,
 * hatch-rarity-aura/ring/core-flash/spark/result-ring) **usunięte w sesji 29B**
 * — komponent renderuje finalny panel z czystymi Tailwind klasami, animacja
 * w pełni w Phaser HatchScene.
 */

export function HatchAnimationModal(): JSX.Element | null {
  const hatchResult = useGameStore((s) => s.hatchResult);
  const setHatchResult = useGameStore((s) => s.setHatchResult);
  const hatchBulkResult = useGameStore((s) => s.hatchBulkResult);
  const setHatchBulkResult = useGameStore((s) => s.setHatchBulkResult);
  const hatchPhase = useGameStore((s) => s.hatchPhase);
  const setHatchPhase = useGameStore((s) => s.setHatchPhase);

  // Defensive: ślad że już wysłaliśmy hatch:start dla aktualnego wyniku —
  // bez tego, re-render hooka (np. inny state change) mógłby ponownie
  // wystartować Phaser cinematic dla tego samego hatchResult'a.
  //
  // Sesja 29A review LOW fix: kluczujemy po `nonce` zamiast `petId+variant+eggDefId`.
  // Stary klucz mógł dawać kolizje przy multi-hatch / powtórnym wykluwaniu
  // identycznego peta — drugi hatch byłby silent-skipped. `nonce` to Date.now()
  // generated w EggInventoryModal — gwarantowanie unikalny per hatch.
  const [startedForNonce, setStartedForNonce] = useState<number | null>(null);

  // ── Start: nowy hatchResult → emit hatch:start + setHatchPhase('animating')
  useEffect(() => {
    if (!hatchResult) {
      // Reset tylko gdy NIE jesteśmy w środku bulk hatcha — bulk ma własny nonce.
      if (!hatchBulkResult) setStartedForNonce(null);
      return;
    }
    if (startedForNonce === hatchResult.nonce) return;   // Już startowane dla tego wyniku

    setStartedForNonce(hatchResult.nonce);
    setHatchPhase('animating');

    // Power liczone live dla payloadu — Phaser potrzebuje do display (gdyby
    // chciał pokazać power w cinematic, choć w sesji A nie pokazuje).
    const power = Math.round(hatchResult.petDef.basePower * VARIANT_MULT[hatchResult.variant]);

    eventBus.emit('hatch:start', {
      hatchNonce: hatchResult.nonce,
      petDef: hatchResult.petDef,
      variant: hatchResult.variant,
      eggDefId: hatchResult.eggDefId,
      isHuge: hatchResult.isHuge,
      power,
    });
  }, [hatchResult, startedForNonce, setHatchPhase, hatchBulkResult]);

  // ── 5.D.3c: Start bulk hatch → emit hatch:start-bulk ─────────────────────
  useEffect(() => {
    if (!hatchBulkResult) {
      if (!hatchResult) setStartedForNonce(null);
      return;
    }
    if (startedForNonce === hatchBulkResult.nonce) return;

    setStartedForNonce(hatchBulkResult.nonce);
    setHatchPhase('animating');

    const petsForPhaser = hatchBulkResult.pets.map((p) => ({
      petDef: p.petDef,
      variant: p.variant,
      isHuge: p.isHuge,
      power: Math.round(p.petDef.basePower * VARIANT_MULT[p.variant]),
    }));

    eventBus.emit('hatch:start-bulk', {
      hatchNonce: hatchBulkResult.nonce,
      pets: petsForPhaser,
      eggDefId: hatchBulkResult.eggDefId,
    });
  }, [hatchBulkResult, startedForNonce, setHatchPhase, hatchResult]);

  // ── Phaser → React: 'hatch:complete' → showResult
  useEffect(() => {
    const off = eventBus.on('hatch:complete', ({ hatchNonce }) => {
      // Shutdown poprzedniego HatchScene może przyjść już po zapisaniu nowego
      // wyniku. Complete starego przebiegu nie może odsłonić panelu nowego.
      const current = useGameStore.getState();
      const currentNonce = Math.max(
        current.hatchResult?.nonce ?? Number.NEGATIVE_INFINITY,
        current.hatchBulkResult?.nonce ?? Number.NEGATIVE_INFINITY,
      );
      if (hatchNonce !== currentNonce) return;
      setHatchPhase('showResult');
    });
    return () => off();
  }, [setHatchPhase]);

  const handleClose = (): void => {
    setHatchResult(null);
    setHatchBulkResult(null);
    setHatchPhase(null);
    eventBus.emit('hatch:closed', {});
  };

  // ── Render gating ─────────────────────────────────────────────────────────
  // null = brak hatcha — nic nie renderujemy
  if (!hatchPhase) return null;
  if (!hatchResult && !hatchBulkResult) return null;
  // animating = Phaser robi cinematic — komponent jest "obecny" w drzewie React
  // (żeby useEffect listenery działały), ale nie renderujemy żadnego UI.
  // App.tsx widzi hatchPhase='animating' i ukrywa pozostałe UI elementy.
  if (hatchPhase === 'animating') return null;

  // ── showResult: bulk grid panel ───────────────────────────────────────────
  if (hatchBulkResult) {
    return <BulkResultPanel result={hatchBulkResult} onClose={handleClose} />;
  }

  // ── showResult: single hatch finalny panel ───────────────────────────────
  if (!hatchResult) return null;
  const { petDef, variant, isHuge } = hatchResult;
  const power = Math.round(petDef.basePower * VARIANT_MULT[variant]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center overflow-hidden bg-black/75 px-4 font-game pointer-events-auto backdrop-blur-[2px]">
      <div className="relative flex w-full max-w-[420px] flex-col items-center justify-center text-center bg-white/95 rounded-3xl p-6 shadow-2xl">
        <PetImage
          textureKey={petDef.art.textureKey}
          biomeId={petDef.origin?.biomeId ?? 'biome-meadow'}
          alt={petDef.name.pl}
        />

        <div className={`text-2xl font-bold mt-3 mb-1 ${rarityTextColor(petDef.rarity)}`}>
          {petDef.name.pl}
        </div>

        <div className="flex items-center justify-center gap-2 mb-3 flex-wrap">
          <span className={`text-sm font-bold px-2 py-0.5 rounded ${rarityBadgeColor(petDef.rarity)}`}>
            {petDef.rarity.toUpperCase()}
          </span>
          {variant !== 'normal' && (
            <span className={`text-sm font-bold px-2 py-0.5 rounded ${variantBadgeColor(variant)}`}>
              {variant.toUpperCase()}
            </span>
          )}
          {isHuge && variant !== 'huge' && (
            <span className="text-sm font-bold px-2 py-0.5 rounded bg-red-500 text-white">
              HUGE
            </span>
          )}
        </div>

        <div className="text-meadow-700 text-base mb-5">
          Moc: <strong>{formatNumberCompact(power)}</strong>
        </div>

        <button
          onClick={handleClose}
          className="bg-meadow-500 hover:bg-meadow-700 active:scale-95 transition rounded-xl text-white text-base font-bold px-8 py-2 shadow-lg"
        >
          Świetnie!
        </button>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

/**
 * Bulk hatch final panel (5.D.3c — faza 5 stand-by + dismiss).
 *
 * Renderowany po `hatch:complete` od Phaser HatchBulkDirector. Grid PetCard
 * z liczbami petów + nowych typów + "Świetnie!" button. Pety w tej samej
 * kolejności rarity-asc co reveal w Phaser, dla wizualnej ciągłości
 * (gracz widzi te same legendary/huge "na końcu" gdzie wjeżdżały).
 */
function BulkResultPanel({
  result,
  onClose,
}: {
  result: HatchBulkResult;
  onClose: () => void;
}): JSX.Element {
  // Sortuj identycznie jak HatchBulkDirector: rarity asc, huge/titanic ostatni
  // (titanic za huge — apex rozmiarowy).
  const bigRank = (p: { isHuge: boolean; variant: string }): number =>
    p.variant === 'titanic' ? 2 : p.isHuge || p.variant === 'huge' ? 1 : 0;
  const sorted = [...result.pets].sort((a, b) => {
    const diff = bigRank(a) - bigRank(b);
    if (diff !== 0) return diff;
    return RARITY_ORDER.indexOf(a.petDef.rarity) - RARITY_ORDER.indexOf(b.petDef.rarity);
  });

  const n = sorted.length;
  // Grid responsywny per N (Codex 5.D.3c MED 2 fix + 5.D.3d MED 1 fix):
  //   2-4    → 2 kolumny zawsze (BIG layout)
  //   5-6    → mobile 2, sm+ 3
  //   7-8    → mobile 2, sm+ 4
  //   9-10   → mobile 2, sm+ 5
  //   11-12  → mobile 2, sm+ 4 (3×4 zgodnie z `bulkGridLayout()` w Phaser
  //           cinematic — bez tego React panel skakał na 5+5+2 zamiast 4×3
  //           dla max level upgrade bulk_hatch)
  // Bez tego dla N=10 + mobile 320px viewport `grid-cols-5` + obraz w-20
  // daje overlap (po `px-4` overlay + `p-6` panel + 4× `gap-3` zostaje
  // ~38 px/kolumnę, image 80 px = overflow).
  // Tailwind explicit klasy (JIT nie renderuje dynamic grid-cols-N).
  const gridColsClass =
    n <= 4 ? 'grid-cols-2'
    : n <= 6 ? 'grid-cols-2 sm:grid-cols-3'
    : n <= 8 ? 'grid-cols-2 sm:grid-cols-4'
    : n <= 10 ? 'grid-cols-2 sm:grid-cols-5'
    : 'grid-cols-2 sm:grid-cols-4';   // 11-12 → 4 kolumny (spójne z Phaser 3×4)

  return (
    <div className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/75 px-4 py-6 font-game pointer-events-auto backdrop-blur-[2px]">
      <div className="relative flex w-full max-w-3xl flex-col items-center bg-white/95 rounded-3xl p-6 shadow-2xl">
        <h2 className="text-2xl font-bold text-meadow-700 mb-1">
          Zdobyłeś {n} {petNounPl(n)}!
        </h2>
        {result.newSpeciesCount > 0 && (
          <div className="text-base text-meadow-600 mb-4">
            <strong>{result.newSpeciesCount}</strong> {newSpeciesNounPl(result.newSpeciesCount)} w kolekcji
          </div>
        )}
        {result.newSpeciesCount === 0 && <div className="mb-4" />}

        <div className={`grid ${gridColsClass} gap-3 w-full mb-5`}>
          {sorted.map((p, i) => (
            <BulkPetCard key={i} entry={p} compact={n > 4} />
          ))}
        </div>

        <button
          onClick={onClose}
          className="bg-meadow-500 hover:bg-meadow-700 active:scale-95 transition rounded-xl text-white text-base font-bold px-10 py-2 shadow-lg"
        >
          Świetnie!
        </button>
      </div>
    </div>
  );
}

/** Karta peta w bulk grid — kompaktowa (mid-size) lub duża (BIG layout). */
function BulkPetCard({
  entry,
  compact,
}: {
  entry: HatchBulkResult['pets'][number];
  compact: boolean;
}): JSX.Element {
  const { petDef, variant, isHuge } = entry;
  const biomeId = petDef.origin?.biomeId ?? 'biome-meadow';
  // Codex 5.D.3c MED 2 fix: compact obraz `w-14` na bazie (320px viewport),
  // dopiero od sm+ rośnie do w-20. Bez tego image 80 px nie mieści się
  // w 2-kolumnowym mobile grid (kolumna ~134 px - padding).
  // Codex 5.D.3c FIXES MED 1: non-compact też mobile-first (`w-24` na bazie),
  // bo 2-4 petów jest normalnym flow (`EggInventoryModal` triggeruje bulk od
  // 2 jajek). Na 320 px kolumna ~114 px, karta z `w-28` (112 px) + `p-2`
  // (16 px) = 128 px → overflow. `w-24` (96 px) + `p-1.5` (12 px) = 108 px
  // mieści się.
  const imgClass = compact ? 'w-14 h-14 sm:w-20 sm:h-20' : 'w-24 h-24 sm:w-32 sm:h-32';
  // Padding karty: mobile mniejsze dla obu wariantów, sm+ standardowe.
  const cardPad = 'p-1.5 sm:p-2';
  return (
    <div className={`flex flex-col items-center text-center rounded-2xl ${cardPad} ${rarityBorderColor(petDef.rarity)}`}>
      <PetImage textureKey={petDef.art.textureKey} biomeId={biomeId} alt={petDef.name.pl} className={`${imgClass} object-contain`} />
      <div className={`mt-1 font-bold ${compact ? 'text-[11px] sm:text-xs' : 'text-sm'} ${rarityTextColor(petDef.rarity)} line-clamp-1`}>
        {petDef.name.pl}
      </div>
      <div className="flex items-center justify-center gap-1 mt-0.5 flex-wrap">
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${rarityBadgeColor(petDef.rarity)}`}>
          {petDef.rarity.toUpperCase()}
        </span>
        {variant !== 'normal' && variant !== 'huge' && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${variantBadgeColor(variant)}`}>
            {variant.toUpperCase()}
          </span>
        )}
        {isHuge && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500 text-white">
            HUGE
          </span>
        )}
      </div>
    </div>
  );
}

const RARITY_ORDER = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'] as const;

function rarityBorderColor(rarity: string): string {
  switch (rarity) {
    case 'common':    return 'border-2 border-gray-300 bg-gray-50';
    case 'uncommon':  return 'border-2 border-green-300 bg-green-50';
    case 'rare':      return 'border-2 border-blue-300 bg-blue-50';
    case 'epic':      return 'border-2 border-purple-300 bg-purple-50';
    case 'legendary': return 'border-2 border-yellow-400 bg-yellow-50';
    case 'mythic':    return 'border-2 border-red-400 bg-red-50';
    default:          return 'border-2 border-gray-200 bg-white';
  }
}

function petNounPl(n: number): string {
  if (n === 1) return 'peta';
  return 'petów';
}

function newSpeciesNounPl(n: number): string {
  if (n === 1) return 'nowy typ';
  if (n >= 2 && n <= 4) return 'nowe typy';
  return 'nowych typów';
}

function PetImage({ textureKey, biomeId, alt, className }: { textureKey: string; biomeId: ID; alt: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  const finalClassName = className ?? 'w-40 h-40 object-contain';
  if (failed) {
    return <div className={`${finalClassName} bg-gray-100 rounded-xl`} aria-label={alt} />;
  }
  return (
    <img
      src={petAssetUrl(textureKey, biomeId)}
      alt={alt}
      className={finalClassName}
      draggable={false}
      onError={() => setFailed(true)}
    />
  );
}

function rarityTextColor(rarity: string): string {
  switch (rarity) {
    case 'common':    return 'text-gray-700';
    case 'uncommon':  return 'text-green-600';
    case 'rare':      return 'text-blue-600';
    case 'epic':      return 'text-purple-600';
    case 'legendary': return 'text-yellow-600';
    case 'mythic':    return 'text-red-600';
    default:          return 'text-gray-700';
  }
}

function rarityBadgeColor(rarity: string): string {
  switch (rarity) {
    case 'common':    return 'bg-gray-500 text-white';
    case 'uncommon':  return 'bg-green-500 text-white';
    case 'rare':      return 'bg-blue-500 text-white';
    case 'epic':      return 'bg-purple-500 text-white';
    case 'legendary': return 'bg-yellow-500 text-white';
    case 'mythic':    return 'bg-red-500 text-white';
    default:          return 'bg-gray-500 text-white';
  }
}

function variantBadgeColor(variant: string): string {
  switch (variant) {
    case 'gold':    return 'bg-yellow-400 text-black';
    case 'rainbow': return 'bg-gradient-to-r from-pink-400 via-yellow-400 to-blue-400 text-white';
    case 'huge':    return 'bg-red-500 text-white';
    case 'titanic': return 'bg-gradient-to-r from-violet-500 via-fuchsia-500 to-indigo-500 text-white';
    default:        return 'bg-gray-300 text-black';
  }
}

/**
 * Eksportowany dla legacy EggImage gdziekolwiek by go używał (currentlly null).
 * EggInventoryModal i EggShopModal mają własne EggImage local components.
 */
export { rarityBadgeColor, rarityTextColor, variantBadgeColor };

/**
 * Cleanup importu — `eggAssetUrl` był używany w starym animatorze. Po
 * refaktorze HatchScene rysuje egg, nie React. Jeśli się ktoś po `eggAssetUrl`
 * dostał z tego pliku, niech znajdzie nowe miejsce.
 */
void EGGS_BY_ID;   // import side-effect reference (no-op, ale przydatne gdyby ktoś chciał egg metadata w panelu)
